import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  InstancedBufferAttribute,
  InstancedMesh,
  MeshBasicNodeMaterial,
  Vector3,
} from 'three/webgpu';
import {
  attribute,
  color,
  float,
  mix,
  positionGeometry,
  smoothstep,
  uniform,
  uv,
  vec3,
} from 'three/tsl';
import { heightAt, type Heightfield } from '@stillsun/shared';
import { renderFlags, type QualityTier } from './renderer.js';
import { terrainBounds } from './terrain.js';

/**
 * The grass sheet: one `InstancedMesh` of cheap crossed quads, scattered once over the terrain
 * and animated entirely in the vertex stage.
 *
 * Everything about this file is shaped by two budget rules (docs/PERF-BUDGET.md):
 *
 * - **One draw, whatever the field holds.** 24,000 blades and 400 blades are the same draw and
 *   the same render object. Nothing here is an `Object3D` but the mesh itself.
 * - **No per-frame CPU.** `update()` writes two uniforms. There is no per-blade loop after
 *   construction, no re-scatter, no sort, no `instanceMatrix` upload. The wind, the distance
 *   fade and the per-blade variation are all functions the GPU evaluates from three instanced
 *   attributes plus a clock.
 *
 * ## Why the instance transform is done by hand
 *
 * `NodeMaterial.setupPosition` applies `instancedMesh(object)` — the built-in `instanceMatrix`
 * transform — and *then*, if the material has a `positionNode`, overwrites `positionLocal` with
 * it. Reading `positionLocal` back inside that node to get the post-instancing value works only
 * by assignment ordering through a varying, and a wrong guess there is a silently mis-transformed
 * vertex or a shader that fails to build with no JavaScript signal (WEBGPU-NOTES §1). So
 * `instanceMatrix` is left identity and the whole placement lives in `positionNode`, built from
 * per-instance attributes (`GPUInputStepMode.Instance`, three.webgpu.js) this file owns. That
 * also hands the shader the one thing the distance fade needs and `instanceMatrix` cannot give
 * it: the blade's **root** position, independent of which vertex is being transformed.
 *
 * ## The distance fade
 *
 * Past `FADE_FAR` a blade's vertices collapse onto its own root, so it covers no pixels at all.
 * That is cheaper than a discard (no fragments to kill), cheaper than culling (no CPU pass, no
 * count change, no re-upload) and continuous, so nothing pops. The blade count is therefore a
 * memory and vertex-fetch budget, not a fill budget: fill is bounded by `FADE_FAR` alone.
 */

export interface GrassField {
  readonly mesh: InstancedMesh;
  /**
   * Advances the wind clock and moves the fade origin. Two uniform writes; cost is independent
   * of the blade count. `viewer` is the *character's* world position, not the camera's — a
   * third-person camera sits several metres back and grass should stand around the player.
   */
  update(dt: number, viewer: Vector3): void;
  dispose(): void;
}

/**
 * Blade budget per tier. A memory/vertex-fetch number, not a fill number (see the fade above):
 * 24,000 blades at 3 quads is 432 k triangles of *capacity*, of which only what falls inside
 * `FADE_FAR` of the player ever rasterises.
 */
export const GRASS_COUNT: Record<QualityTier, number> = {
  low: 4_000,
  pc: 20_000,
  ultra: 32_000,
};

/** Quads per blade. Two crossed sheets read as volume from any angle; a third fills the tuft. */
const QUADS: Record<QualityTier, number> = { low: 2, pc: 3, ultra: 3 };

/** Blade size range, metres. */
const BLADE_W_MIN = 0.06;
const BLADE_W_MAX = 0.13;
const BLADE_H_MIN = 0.22;
const BLADE_H_MAX = 0.52;

/** Where the fade starts and where a blade has collapsed to nothing, metres from `viewer`. */
const FADE_NEAR = 26;
const FADE_FAR = 42;

/** Wind: metres of tip travel, radians per second of clock, and the direction it blows. */
const WIND_AMP = 0.16;
const WIND_SPEED = 1.6;
const WIND_DIR_X = 0.86;
const WIND_DIR_Z = 0.51;
/** Spatial frequency of the gust front, in radians per metre. ~35 m between crests. */
const WIND_FREQ_X = 0.14;
const WIND_FREQ_Z = 0.11;

/** Blade albedo, root to tip. Dry, olive, inside the Stillsun palette — not a lawn. */
const BLADE_ROOT = 0x2f3a1e;
const BLADE_TIP = 0x6d7a41;

/** Below this splat weight the ground is not grass and no blade is planted. */
const DENSITY_FLOOR = 0.12;

/**
 * TSL's published types widen the `attribute(name, type)` type parameter to `string`, which
 * loses every swizzle on the result. Same escape hatch render/decals.ts and render/sky.ts use.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyNode = any;

/** Deterministic pseudo-random, so a field is identical between reloads and between clients. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0x100000000;
  };
}

/**
 * One blade: `quads` sheets through the same vertical axis, evenly spread in yaw. Local space is
 * a unit blade — x in [-0.5, 0.5], y in [0, 1] — so the per-instance width and height scale it
 * directly, and `uv.y` is the tip weight the wind and the colour gradient both read.
 */
function bladeGeometry(quads: number): BufferGeometry {
  const positions = new Float32Array(quads * 4 * 3);
  const uvs = new Float32Array(quads * 4 * 2);
  const indices = new Uint16Array(quads * 6);
  for (let q = 0; q < quads; q++) {
    const a = (q / quads) * Math.PI; // half a turn: a quad is already two-sided
    const dx = Math.cos(a) * 0.5;
    const dz = Math.sin(a) * 0.5;
    const v = q * 4;
    // 0 bottom-left, 1 bottom-right, 2 top-right, 3 top-left
    const corners: [number, number, number][] = [
      [-dx, 0, -dz],
      [dx, 0, dz],
      [dx, 1, dz],
      [-dx, 1, -dz],
    ];
    const cornerUvs: [number, number][] = [
      [0, 0],
      [1, 0],
      [1, 1],
      [0, 1],
    ];
    for (let i = 0; i < 4; i++) {
      const c = corners[i] ?? [0, 0, 0];
      const t = cornerUvs[i] ?? [0, 0];
      positions[(v + i) * 3] = c[0];
      positions[(v + i) * 3 + 1] = c[1];
      positions[(v + i) * 3 + 2] = c[2];
      uvs[(v + i) * 2] = t[0];
      uvs[(v + i) * 2 + 1] = t[1];
    }
    const o = q * 6;
    indices[o] = v;
    indices[o + 1] = v + 1;
    indices[o + 2] = v + 2;
    indices[o + 3] = v;
    indices[o + 4] = v + 2;
    indices[o + 5] = v + 3;
  }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new BufferAttribute(positions, 3));
  geo.setAttribute('uv', new BufferAttribute(uvs, 2));
  geo.setIndex(new BufferAttribute(indices, 1));
  // The material is unlit, so no normals are needed; leaving them off saves a whole attribute
  // stream over every blade vertex.
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  return geo;
}

export interface GrassOptions {
  /** Blade budget. Fewer are placed if the mask rejects too much ground. */
  count: number;
  /**
   * The grass splat weight at a world XZ, 0..1 — pass `terrain.ts`'s `grassDensityAt` bound to
   * the map's heightfield. Taken as a parameter rather than imported so the scatter mask and the
   * terrain paint can never drift apart *and* this module never reaches into terrain internals.
   */
  density: (x: number, z: number) => number;
  tier: QualityTier;
  /**
   * Optional per-cell density mask, 0..1, on **exactly** `hf`'s lattice (`cols * rows` values in
   * row-major order) — i.e. `RegionSidecar.grassMask`, which the Blender region pass writes so
   * the client does not have to re-derive where the road, the paving, the rock and the barrows
   * are. Sampled bilinearly and multiplied into `density`, so it thins grass at a paving edge
   * rather than cutting a hard line along one.
   *
   * Omitted (or the wrong length, which is logged and ignored) leaves the scatter exactly as it
   * was: the multiply is skipped entirely, not applied as 1, so a field with no mask is bit for
   * bit the field this file scattered before the mask existed.
   */
  mask?: readonly number[];
}

/**
 * Bilinear sample of a per-cell mask laid out on `hf`'s own lattice. Deliberately the same
 * indexing and the same edge clamp as `@stillsun/shared`'s `heightAt`: the mask and the ground it
 * masks have to agree cell for cell, and re-deriving the arithmetic here is how they would drift.
 */
function maskAt(mask: readonly number[], hf: Heightfield, x: number, z: number): number {
  const fx = (x - hf.originX) / hf.cellSize;
  const fz = (z - hf.originZ) / hf.cellSize;
  const cx = Math.min(hf.cols - 2, Math.max(0, Math.floor(fx)));
  const cz = Math.min(hf.rows - 2, Math.max(0, Math.floor(fz)));
  const u = Math.min(1, Math.max(0, fx - cx));
  const v = Math.min(1, Math.max(0, fz - cz));
  const i = cz * hf.cols + cx;
  const m00 = mask[i] ?? 0;
  const m10 = mask[i + 1] ?? m00;
  const m01 = mask[i + hf.cols] ?? m00;
  const m11 = mask[i + hf.cols + 1] ?? m00;
  const a = m00 + (m10 - m00) * u;
  const b = m01 + (m11 - m01) * u;
  return a + (b - a) * v;
}

/**
 * Scatters a bounded grass field over `hf`. Blades sit exactly on the sim's heightfield (the
 * same `heightAt` the server walks), so nothing floats or sinks.
 */
export function createGrass(hf: Heightfield, opts: GrassOptions): GrassField {
  const { minX, maxX, minZ, maxZ } = terrainBounds(hf);
  const capacity = Math.max(1, Math.floor(opts.count));
  const geometry = bladeGeometry(QUADS[opts.tier]);

  // Rejection sampling against the mask. Capped attempts, because a map whose terrain the splat
  // paints as all rock would otherwise spin here: the field simply comes out smaller.
  const base = new Float32Array(capacity * 3);
  const param = new Float32Array(capacity * 4);
  const rand = rng(0x9e37_79b9);
  let placed = 0;
  const spanX = maxX - minX;
  const spanZ = maxZ - minZ;
  // Validated once, here, rather than per sample: a mask on the wrong lattice is a data bug and
  // must be loud, but it must not cost a grass field (props.ts's fail-soft rule).
  let mask = opts.mask ?? null;
  if (mask !== null && mask.length !== hf.cols * hf.rows) {
    console.warn(
      `[stillsun] grass mask is ${String(mask.length)} cells, expected ${String(hf.cols * hf.rows)}; ignoring it`,
    );
    mask = null;
  }
  for (let attempt = 0; attempt < capacity * 8 && placed < capacity; attempt++) {
    const x = minX + rand() * spanX;
    const z = minZ + rand() * spanZ;
    // The mask multiplies the splat weight, so it thins *and* shortens the blades it survives
    // (see the height line below) — grass fading out toward a kerb rather than stopping at one.
    const weight = mask === null ? opts.density(x, z) : opts.density(x, z) * maskAt(mask, hf, x, z);
    if (weight < DENSITY_FLOOR || rand() > weight) continue;
    base[placed * 3] = x;
    base[placed * 3 + 1] = heightAt(hf, x, z);
    base[placed * 3 + 2] = z;
    // Denser ground grows taller blades, so the mask reads in silhouette and not only in count.
    const yaw = rand() * Math.PI * 2;
    param[placed * 4] = BLADE_W_MIN + rand() * (BLADE_W_MAX - BLADE_W_MIN);
    param[placed * 4 + 1] =
      (BLADE_H_MIN + rand() * (BLADE_H_MAX - BLADE_H_MIN)) * (0.6 + weight * 0.4);
    param[placed * 4 + 2] = Math.cos(yaw);
    param[placed * 4 + 3] = Math.sin(yaw);
    placed++;
  }

  geometry.setAttribute('iBase', new InstancedBufferAttribute(base, 3));
  geometry.setAttribute('iParam', new InstancedBufferAttribute(param, 4));

  // The wind clock and the fade origin: the only two things `update` touches, and the only two
  // things that change after construction. Uniforms, not node-graph edits — a rebuilt graph
  // changes the material's cache key and recompiles the shader (WEBGPU-NOTES §1).
  const clock = uniform(0);
  const viewerPos = uniform(new Vector3((minX + maxX) / 2, 0, (minZ + maxZ) / 2));

  // Unlit on purpose. A `MeshBasicNodeMaterial` builds no lights node, so the grass shader does
  // not depend on how many lights are in the scene and cannot be re-keyed by one changing
  // (PERF-BUDGET rule 3) — which matters more here than anywhere, because this is the material
  // covering the most pixels. Scene fog still applies: `NodeMaterial` wires it from `scene.fog`.
  const material = new MeshBasicNodeMaterial({ side: DoubleSide });
  try {
    const iBase = attribute('iBase', 'vec3') as unknown as AnyNode;
    const iParam = attribute('iParam', 'vec4') as unknown as AnyNode;
    const local = positionGeometry as unknown as AnyNode;
    const tip: AnyNode = uv().y;

    const width = iParam.x;
    const height = iParam.y;
    const cosYaw = iParam.z;
    const sinYaw = iParam.w;

    // Scale, then yaw about +Y. Written out rather than through a matrix so it is four
    // multiplies and two adds in the vertex stage.
    const sx = local.x.mul(width);
    const sz = local.z.mul(width);
    const rx = sx.mul(cosYaw).add(sz.mul(sinYaw));
    const rz = sz.mul(cosYaw).sub(sx.mul(sinYaw));
    const offset: AnyNode = vec3(rx, local.y.mul(height), rz);

    let bent: AnyNode = offset;
    if (renderFlags.noise) {
      // A travelling gust: one low-frequency wave over world XZ plus a faster harmonic, so the
      // field ripples in bands instead of every blade waving in unison. The phase is spatial, so
      // the per-blade offset comes free from `iBase` — no extra attribute, no per-blade CPU.
      const phase: AnyNode = clock.add(iBase.x.mul(WIND_FREQ_X)).add(iBase.z.mul(WIND_FREQ_Z));
      const sway: AnyNode = phase.sin().mul(0.72).add(phase.mul(2.3).add(1.7).sin().mul(0.28));
      // Quadratic in the tip weight: the root is pinned, the tip carries the whole travel, and
      // the curve between them is what makes a blade bend rather than shear.
      const amp: AnyNode = sway.mul(tip).mul(tip).mul(height).mul(WIND_AMP);
      bent = offset.add(vec3(amp.mul(WIND_DIR_X), float(0), amp.mul(WIND_DIR_Z)));
    }

    // 1 within FADE_NEAR, 0 past FADE_FAR. Ascending edges plus `oneMinus`: a descending
    // `smoothstep` is undefined behaviour in both shading languages (the house idiom, see
    // render/materials.ts). Applied to the blade's offset from its own root, so a faded blade is
    // a degenerate point at ground level and rasterises nothing.
    const dist: AnyNode = iBase.xz.sub((viewerPos as unknown as AnyNode).xz).length();
    const fade: AnyNode = smoothstep(FADE_NEAR, FADE_FAR, dist).oneMinus();

    material.positionNode = iBase.add(bent.mul(fade));
    material.colorNode = mix(color(BLADE_ROOT), color(BLADE_TIP), tip.mul(tip));
  } catch (err) {
    // Fail soft, like every other material in render/: a flat green card beats a black screen.
    console.warn('[stillsun] TSL grass shading unavailable, using flat blades', err);
    material.color.setHex(BLADE_TIP);
  }

  const mesh = new InstancedMesh(geometry, material, capacity);
  mesh.name = 'grass';
  mesh.count = Math.max(1, placed);
  // One bounded field that covers the whole map: the object-level frustum test would never
  // reject it and its bounding sphere would have to span the terrain anyway. The per-blade fade
  // above is the culling.
  mesh.frustumCulled = false;
  // Grass neither casts nor receives. A shadow pass over 400 k triangles of blades to draw
  // shadows nobody can resolve at a 4.5° sun is the definition of a pass that scales with
  // content, and receiving would pull a lights node back into the material.
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  // `instanceMatrix` stays identity — the transform is in `positionNode` (see the header). Its
  // buffer is still uploaded once, which is the price of the `InstancedMesh` the interface asks
  // for; it is 64 bytes per blade and never touched again.

  const field: GrassField = {
    mesh,
    update(dt: number, viewer: Vector3): void {
      clock.value += dt * WIND_SPEED;
      // Wrapped so the phase never grows large enough to lose float precision in a long session
      // (2π * 4096 is ~7 hours at this speed, and the sine is periodic, so this is invisible).
      if (clock.value > Math.PI * 8192) clock.value -= Math.PI * 8192;
      (viewerPos.value as Vector3).copy(viewer);
    },
    dispose(): void {
      geometry.dispose();
      material.dispose();
      mesh.dispose();
    },
  };
  // The scene teardown in render/scene.ts disposes whatever a `mapRoot` child hangs here.
  mesh.userData['dispose'] = () => {
    field.dispose();
  };
  return field;
}
